"use client"

import { useState } from "react"
import { preconnect } from "react-dom"
import MuxPlayer from "@/components/marketing/lazy-mux-player"

import { ACCENT, COLORS } from "@/lib/design-tokens"
import { MUX_PLAYBACK_RATES } from "@/lib/mux-player-config"
import { cn } from "@/lib/utils"

// One branded Mux player for any page that has its own video. There is no
// default asset and no shared clip: every call site names the playback id it
// plays, so a page can never inherit someone else's video.
//
// Branded error/retry state: a playback failure that isn't the fixed iOS/mse
// issue (network blip, a future Mux incident) used to leave Mux Player's own
// bare native error chrome, or a black box, with no recovery affordance.
// `retryKey` remounts the player fresh on retry - Mux Player has no public
// reload() method, so bumping a React `key` is the reliable way to force a
// clean remount.
export function MuxVideoSlot({
  className,
  aspect = "video",
  playbackId,
  videoId,
  videoTitle,
  onActivate,
  autoActivate = false,
}: {
  className?: string
  /** The frame follows the ASSET, so a 9:16 phone recording passes "portrait"
   *  and gets a 9:16 frame - no head is cropped (MEDIA.md law 4) and a
   *  landscape frame never pillarboxes it into black bars. */
  aspect?: "video" | "portrait"
  /** Which Mux asset to play. Required: the player owns the poster,
   *  tap-to-play, error/retry and playback-rate behaviour, and the page owns
   *  which video that behaviour wraps. */
  playbackId: string
  /** Mux analytics labels. They travel with the playback id, so a page passing
   *  its own video also names it - otherwise every view on every page reports
   *  under one label and the Mux dashboard cannot tell them apart. */
  videoId: string
  videoTitle: string
  /** Fired the instant the visitor taps the poster, before the heavy player
   *  chunk loads. The floating VSL listens for it so it can tell a video he
   *  actually started from a poster he merely scrolled past. */
  onActivate?: () => void
  /** When true, the player mounts without a tap and autoplays MUTED (the VSL
   *  scroll-into-view behavior). A real tap always wins: a visitor who taps
   *  the poster gets sound, and this flag then changes nothing. */
  autoActivate?: boolean
}) {
  const [hasError, setHasError] = useState(false)
  const [retryKey, setRetryKey] = useState(0)
  // The poster below is a cross-origin fetch from image.mux.com; warming the
  // connection (DNS + TLS) as soon as a page renders a video slot shaves that
  // handshake off the poster's load. Scoped here so only pages with a video
  // pay for the hint. React dedupes repeated calls.
  preconnect("https://image.mux.com")
  // Tap-to-play poster (owner-approved 2026-07-23 speed interview): the Mux
  // thumbnail paints instantly as a plain image and the heavy player chunk
  // loads only when the visitor taps play. autoPlay on the mounted player
  // makes the tap feel like pressing play, not opening a widget.
  const [activated, setActivated] = useState(false)
  // Derived, not an effect: a tap (activated) always outranks the auto path,
  // so a tapped video plays with sound while a scrolled-into one plays muted.
  const showPlayer = activated || autoActivate
  const autoPlayMode: boolean | "muted" = activated ? true : "muted"
  // A 9:16 frame at full column width becomes a tower, so a portrait clip caps
  // its own width rather than relying on each page to wrap it. `cn` runs
  // tailwind-merge, so a caller passing its own max-w still wins.
  const portraitWidth = aspect === "portrait" && "mx-auto w-full max-w-[380px]"

  return (
    <div
      className={cn(
        "overflow-hidden rounded-[var(--radius-xl)] border border-white/12 bg-black",
        portraitWidth,
        className,
      )}
    >
      <div className={cn("relative w-full bg-black", aspect === "portrait" ? "aspect-[9/16]" : "aspect-video")}>
        {!showPlayer ? (
          <button
            type="button"
            onClick={() => {
              setActivated(true)
              onActivate?.()
            }}
            aria-label="Play the video"
            className="group relative block h-full w-full cursor-pointer"
          >
            {/* eslint-disable-next-line @next/next/no-img-element -- external Mux thumbnail, painted eagerly as the poster */}
            <img
              src={`https://image.mux.com/${playbackId}/thumbnail.webp?time=1&width=960`}
              alt=""
              className="h-full w-full object-cover"
              fetchPriority="high"
            />
            <span className="absolute inset-0 flex items-center justify-center">
              <span className="flex h-16 w-16 items-center justify-center rounded-full bg-black/60 transition-transform group-hover:scale-105">
                <svg viewBox="0 0 24 24" className="ml-1 h-7 w-7 fill-white" aria-hidden>
                  <path d="M8 5v14l11-7z" />
                </svg>
              </span>
            </span>
          </button>
        ) : hasError ? (
          <div className="flex h-full w-full flex-col items-center justify-center gap-3 px-6 text-center">
            <p className="text-[0.95rem] font-medium leading-snug text-white">
              This video didn&apos;t load. Try again.
            </p>
            <button
              type="button"
              onClick={() => {
                setHasError(false)
                setRetryKey((n) => n + 1)
              }}
              className="rounded-full border border-white/25 px-4 py-1.5 text-[0.85rem] font-semibold text-white transition-colors hover:border-white/50"
            >
              Retry
            </button>
          </div>
        ) : (
          <MuxPlayer
            key={retryKey}
            streamType="on-demand"
            playbackId={playbackId}
            preload="metadata"
            autoPlay={autoPlayMode}
            playbackRates={MUX_PLAYBACK_RATES}
            thumbnailTime={1}
            metadata={{
              video_id: videoId,
              video_title: videoTitle,
            }}
            primaryColor={COLORS.paper}
            secondaryColor={COLORS.ink}
            accentColor={ACCENT}
            style={{ height: "100%", width: "100%" }}
            onError={() => setHasError(true)}
          />
        )}
      </div>
    </div>
  )
}
